import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button, Modal } from '@/shared/ui';
import { tendersApi } from '@/shared/api/endpoints/tenders';

const DISMISS_KEY = 'tenderts.starterWelcome.dismissed';
const TARIFF_PATH = '/app/tenders/tariff';
const SKIP_PREFIXES = [
  '/app/tenders/onboarding',
  '/app/verify-email',
  '/login',
  '/register',
];

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function markDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* ignore */
  }
}

function shouldSkipPath(pathname: string): boolean {
  return SKIP_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * One-time welcome after signup: cabinet is on СТАРТ (starter) tariff.
 */
export function StarterWelcomeModal() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const skip = shouldSkipPath(location.pathname);
  const { data, isSuccess, isError } = useQuery({
    queryKey: ['tender-tariff', 'starter-welcome'],
    queryFn: () => tendersApi.tariff(),
    enabled: !skip && !wasDismissed(),
    staleTime: 60_000,
    retry: false,
  });

  const planCode = (data?.subscription?.plan_code ?? '').trim().toLowerCase();
  const planTitle = (data?.subscription?.plan_title ?? '').trim();
  const isStarter = isSuccess && planCode === 'starter';

  useEffect(() => {
    if (skip || isError || !isStarter || wasDismissed()) {
      setOpen(false);
      return;
    }
    setOpen(true);
  }, [skip, isError, isStarter, location.pathname]);

  const onClose = () => {
    markDismissed();
    setOpen(false);
  };

  const onOpenTariff = () => {
    markDismissed();
    setOpen(false);
    navigate(TARIFF_PATH);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('tenders.starterWelcomeTitle')}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.later')}
          </Button>
          <Button type="button" onClick={onOpenTariff}>
            {t('tenders.starterWelcomeCta')}
          </Button>
        </>
      }
    >
      <p className="text-sm text-text-secondary">
        {t('tenders.starterWelcomeBody', {
          plan: planTitle || t('tenders.starterWelcomePlanFallback'),
        })}
      </p>
    </Modal>
  );
}

export const STARTER_WELCOME_DISMISS_KEY = DISMISS_KEY;
